import { cloneDeep, isBoolean, isFunction, isNull, isNumber } from 'lodash';
import { v4 as uuid } from 'uuid';
import {
    deepDeletePrivateProps,
    IDocumentHistoryItem,
    IHistoryCustomData,
    IHistoryDefaultData,
    IHistoryItem,
    IHistorySyncOptions,
    parseLayerId,
} from '@/framework';
import { Framework } from '@/framework/instance';
import { applyDifferentProps, getDifferentProps } from '@/utils/layer-kit';
import { traverseTrees } from '@/utils/tree-kit';
import { ILayerProps } from '@/version-types';
import { IEditMessage } from '../types';
import { OperateType } from '../types/operate';
import { Collaborators } from './collaborators';
import { Room } from './room';

export class CRDTDocument extends Framework {
    /** 撤销、重做临时存储 */
    private redoUndoStore: Map<number, any> = new Map();
    /** 撤销、重做临时存储 */
    private redoUndoStageDirtyStore: Map<number, boolean> = new Map();
    /** 同步的回调钩子 */
    private syncOptions: Record<string, IHistorySyncOptions> = {};
    /** 默认历史记录钩子名称 */
    private defaultHistoryHooksKey = 'default-history-hooks';
    /** 是否准备就绪 */
    private isReady = false;
    /** 历史记录缓存 */
    private historyBuffer: IDocumentHistoryItem[] = [];

    constructor(
        identity: string,
        private room: Room,
        private collaborators: Collaborators,
    ) {
        super(identity);
        this.overrideRegisterMethod();
        this.overrideWriteMethod();
        this.overrideSendMethod();
        this.overrideInsertMethods();
        this.registerDefaultHistoryHooks();
        this.bindOnAfterWrite();
        this.bindOnBeforeRedoUndo();
        this.bindOnAfterRedoUndo();
        this.bindOnMessage();
    }

    /** 销毁 */
    destroy() {
        this.isReady = false;
        this.historyBuffer = [];
    }

    /** 设置就绪 */
    setReady() {
        while (this.historyBuffer.length) {
            const item = this.historyBuffer.shift();
            if (item) this.processMessage(item, false);
        }
        this.isReady = true;
    }

    /** 重写注册方法 */
    private overrideRegisterMethod() {
        console.log('ot overrideRegisterMethod');

        const originalRegister = this.framework.history.register;
        this.framework.history.register = (...args: any) => {
            originalRegister.apply(this.framework.history, args);
            const [type, options] = args;
            if (!isFunction(options)) {
                this.syncOptions[type] = options as IHistorySyncOptions;
            }
        };
    }

    /** 重写写入方法 */
    private overrideWriteMethod() {
        // 写入记录，清空撤销的临时值
        const originalWrite = this.framework.history.write;
        this.framework.history.write = (...args: any) => {
            originalWrite.apply(this.framework.history, args);
            this.redoUndoStore.clear();
            this.redoUndoStageDirtyStore.clear();
        };
    }

    /** 重写发送记录步骤 */
    private overrideSendMethod() {
        this.framework.history.realEdit.send = (type: string, data: any) => {
            const res = this.syncOptions[type]?.onSend?.(data);
            if (res) {
                const { message, isThrottle, force } = res;
                const sendData = [
                    type,
                    [
                        1, // isSend
                        0, // isWrite
                        0, // isRedo
                        0, // isUndo,
                    ],
                    message,
                ];
                if (isThrottle) {
                    this.room.throttleSendToAll(
                        { sendType: OperateType.HistoryCustom, throttleType: type },
                        sendData,
                        force,
                    );
                } else {
                    this.room.sendToAll(OperateType.HistoryCustom, sendData);
                }
            }
        };

        // 发送图层差异数据
        this.framework.history.realEdit.sendDiff = (data: any) => {
            const serverTime = this.room.convertToServerTime(Date.now());
            this.processDiffInfoVersion(data, serverTime);
            console.log('ot send diff', data, serverTime);
            this.room.sendToAll(OperateType.HistoryDefault, [[1, 0, 0], data, serverTime, 'send-diff']);
        };

        // 发送完整图层
        this.framework.history.realEdit.sendLayer = (layer: ILayerProps) => {
            deepDeletePrivateProps(layer);
            const newLayer = { ...layer };
            console.log('ot 发送图层', newLayer);
            const latestLayer = this.framework.getLayer(layer.id);
            const serverTime = this.room.convertToServerTime(Date.now());
            if (latestLayer) {
                latestLayer.__renderLayerVersion = serverTime;
            }
            this.room.sendToAll(OperateType.SyncLayer, [newLayer, serverTime]);
        };
        this.room.onMessage(OperateType.SyncLayer, ({ data }: IEditMessage<[ILayerProps, number]>) => {
            const [layer, serverTime] = data;
            console.log('ot 接收图层', serverTime, layer);
            const latestLayer = this.framework.getLayer(layer.id, true);
            if (latestLayer) {
                const newLayer: any = {};
                Object.assign(newLayer, latestLayer, layer);
                if (JSON.stringify(newLayer) !== JSON.stringify(latestLayer)) {
                    console.log('ot 更新图层', newLayer);
                    newLayer.__renderLayerVersion = serverTime;
                    this.framework.stage.addOrUpdate(newLayer);
                    this.framework.createFontFamilySubset([this.framework.getLayer(newLayer.id)], true);
                }
            }
        });
    }

    /** 重写插入方法 */
    private overrideInsertMethods() {
        // 插入的队列
        let insertQueue: {
            id: string;
            wsKey: number;
            count: number;
            isPending?: boolean;
            resolve?: any;
        }[] = [];

        // 处理插入队列
        const processInsertQueue = () => {
            if (!this.collaborators.isMaster()) return;
            if (!insertQueue.length) return;
            const item = insertQueue.find((item) => item.isPending);
            if (item) return;
            const pendingItem = insertQueue[0];
            pendingItem.isPending = true;
            const originIndexs: number[] = [];
            const length = this.framework.getOriginIdsList().length;
            for (let i = 0; i < pendingItem.count; i++) {
                originIndexs.push(length + i);
            }
            this.room.sendToAll(OperateType.StageInsertPending, [pendingItem.id, originIndexs]);
            pendingItem.resolve?.({
                originIndexs,
                insertId: pendingItem.id,
            });
            delete pendingItem.resolve;
        };

        // 重写插入舞台结束
        this.framework.history.realEdit.insertStageEnd = (insertId: string) => {
            insertQueue = insertQueue.filter((item) => item.id !== insertId);
            processInsertQueue();
        };

        const originInsertStage = this.framework.history.realEdit.insertStage;
        // 重写获取插入的原始索引
        this.framework.history.realEdit.insertStage = (count: number) => {
            return new Promise((resolve) => {
                if (!this.room.isRealEdit()) {
                    originInsertStage(count).then((res) => resolve(res));
                    return;
                }
                const data = {
                    id: uuid(),
                    count,
                    wsKey: this.collaborators.getWsKey(),
                    resolve,
                };
                insertQueue.push(data);
                this.room.sendToAll(OperateType.StageInsert, [count, data.id, data.wsKey]);
                processInsertQueue();
            });
        };
        // 监听成员变化
        this.room.onMembersChange(() => {
            const onlineKeys = new Set(this.collaborators.getMembers().map((member) => member.getInfo().wsKey));
            insertQueue = insertQueue.filter((item) => onlineKeys.has(item.wsKey));
            processInsertQueue();
        });

        // 接收到插入数据
        this.room.onMessage(OperateType.StageInsertPending, ({ data }: IEditMessage<[string, number[]]>) => {
            const [insertId, originIndexs] = data;
            if (!insertQueue.length) return;
            const item = insertQueue.find((item) => item.id === insertId);
            if (!item) return;
            item.isPending = true;
            item.resolve?.({
                originIndexs,
                insertId: item.id,
            });
            delete item.resolve;
        });

        // 接收到插入请求
        this.room.onMessage(OperateType.StageInsert, ({ data }: IEditMessage<[number, string, number]>) => {
            const [count, id, wsKey] = data;
            insertQueue.push({
                id,
                count,
                wsKey,
            });
            processInsertQueue();
        });
    }

    /** 注册默认历史记录钩子 */
    private registerDefaultHistoryHooks() {
        // 重写默认的撤销、重做
        const originalDefaultRedoUndo = this.framework.history.defaultRedoUndo;
        this.framework.history.defaultRedoUndo = (
            historyItem: IHistoryItem<IHistoryDefaultData>,
            isRedo: boolean,
            isUndo: boolean,
        ) => {
            // 单人模式
            if (!(this.room.isRealEdit() && this.framework.history.isMainHistory())) {
                originalDefaultRedoUndo.apply(this.framework.history, [historyItem, isRedo, isUndo]);
                return;
            }
            // 多人模式
            this.syncOptions[this.defaultHistoryHooksKey].onRedoUndo(historyItem, isRedo, isUndo);
        };

        // 注册默认历史记录钩子
        this.syncOptions[this.defaultHistoryHooksKey] = {
            onRedoUndo: (historyItem: IHistoryItem<IHistoryDefaultData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const data = historyItem.data;
                // console.log('ot onRedoUndo 1', cloneDeep(data));
                const diffInfo = this.getDifferentInfo(data, isRedo, isUndo);
                // console.log('ot onRedoUndo 2', cloneDeep(diffInfo));
                const originLayerRecord: Record<string, ILayerProps> = {};
                if (data.newLayer && data.oldLayer) {
                    if (data.newLayer.layerType !== data.oldLayer.layerType) {
                        originLayerRecord[data.newLayer.id] = isUndo ? data.oldLayer : data.newLayer;
                    }
                } else if (data.newLayers && data.oldLayers) {
                    const layersRecord: Record<string, ILayerProps> = {};
                    data.newLayers.forEach((layer) => {
                        layersRecord[layer.id] = layer;
                    });
                    data.oldLayers.forEach((oldLayer) => {
                        const newLayer = layersRecord[oldLayer.id];
                        if (newLayer.layerType !== oldLayer.layerType) {
                            originLayerRecord[oldLayer.id] = isUndo ? oldLayer : newLayer;
                        } else {
                            delete layersRecord[oldLayer.id];
                        }
                    });
                }
                this.processDifferentInfo(diffInfo, false, originLayerRecord).forEach((layerId) => {
                    this.framework.operate.tryRefreshActiveLayer(layerId);
                });
            },
            onMessage: (message: Record<string, any>) => {
                this.processDifferentInfo(message, true).forEach((layerId) => {
                    this.framework.operate.tryRefreshActiveLayer(layerId);
                    const layer = this.framework.getLayer(layerId);
                    if (layer) {
                        this.framework.createFontFamilySubset([layer], true);
                    }
                });
            },
        };
    }

    /** 绑定写入记录后的事件 */
    private bindOnAfterWrite() {
        // 写入记录后，发送给其他用户
        this.framework.history.onAfterWrite(({ isMain, data, isCustom }, historyItem) => {
            if (this.room.isRealEdit() && isMain) {
                historyItem.serverTime = this.room.convertToServerTime(historyItem.time);
                let type = this.defaultHistoryHooksKey;
                if (isCustom) {
                    type = (data as IHistoryCustomData).type;
                }
                console.log('ot 写入发送', type, historyItem.id, historyItem.serverTime, {
                    id: historyItem.id,
                    serverTime: historyItem.serverTime,
                    data: cloneDeep(historyItem),
                    isWrite: true,
                    isRedo: false,
                    isUndo: false,
                    type,
                    isLocal: true,
                });
                if (isCustom) {
                    const { type } = data as IHistoryCustomData;
                    const res = this.syncOptions[type]?.onAfterWrite?.(historyItem);
                    if (isNull(res)) return;
                    this.syncOptions[type]?.onMerge?.(historyItem, true);
                    if (this.syncOptions[type]?.isLikeDefault && res?.message[0]) {
                        // console.log('ot 自定义历史记录 2', res, historyItem.serverTime);
                        this.processDiffInfoVersion(res.message[0], historyItem.serverTime);
                    }
                    const operateData = [
                        0, // isSend
                        1, // isWrite
                        0, // isRedo
                        0, // isUndo
                    ];
                    if (historyItem.needSyncRenderIds) {
                        this.room.syncRenderIdsAdapter.set(this.room.collaborators.getWsKey(), historyItem.originIndex);
                        operateData.push(historyItem.originIndex);
                    }
                    if (res) {
                        const { message } = res;
                        this.room.sendToAll(OperateType.HistoryCustom, [
                            type,
                            operateData,
                            message,
                            null,
                            historyItem.serverTime,
                            historyItem.id,
                        ]);
                    } else {
                        this.room.sendToAll(OperateType.HistoryCustom, [type, operateData, historyItem]);
                    }
                    return;
                }
                // 默认历史记录
                const diffInfo = this.getDifferentInfo(data, true, false);
                if (!Object.keys(diffInfo).length) return;
                this.processDiffInfoVersion(diffInfo, historyItem.serverTime);
                this.room.sendToAll(OperateType.HistoryDefault, [
                    [
                        1, // isWrite
                        0, // isRedo
                        0, // isUndo
                    ],
                    diffInfo,
                    historyItem.serverTime,
                    historyItem.id,
                ]);
            }
        });
    }

    /** 绑定撤销、重做前的钩子，预处理数据 */
    private bindOnBeforeRedoUndo() {
        // 多人协作，撤销记录当前最新值，重做时候覆盖用最新值
        this.framework.history.onBeforeChange(({ isMain, data, isCustom, isRedo, isUndo }, historyItem) => {
            if (this.room.isRealEdit() && isMain) {
                historyItem.isRealEditMode = true;
                historyItem.serverTime = this.room.convertToServerTime(Date.now());
                const historyCursor = historyItem.cursor;
                const originIndex = historyItem.originIndex;
                const skipCheckOriginIndex = historyItem.skipCheckOriginIndex;
                if (!skipCheckOriginIndex) {
                    // 检查对应的画布是否正存在于渲染列表中
                    if (isUndo) {
                        const isRender = this.framework.checkStageIsRender(originIndex);
                        historyItem.isDirty = !isRender;
                        this.redoUndoStageDirtyStore.set(historyCursor, !isRender);
                    }
                    if (isRedo) {
                        const beforeData = this.redoUndoStageDirtyStore.get(historyCursor);
                        if (!isBoolean(beforeData)) historyItem.isDirty = true;
                        else historyItem.isDirty = beforeData;
                        if (!historyItem.isDirty) {
                            historyItem.isDirty = !this.framework.checkStageIsRender(originIndex);
                        }
                    }
                    if (historyItem.isDirty) return;
                }
                // 自定义历史记录
                if (isCustom) {
                    const res = data as IHistoryCustomData;
                    this.syncOptions[res.type]?.onBeforeRedoUndo?.(historyItem, isRedo, isUndo, this.redoUndoStore);
                    return;
                }
                // 默认历史记录
                const { newLayer, newLayers, oldLayer, oldLayers } = data as IHistoryDefaultData;
                let activeId = '';
                if (isUndo) {
                    const oldFlatLayers: ILayerProps[] = [];
                    const newFlatLayers: ILayerProps[] = [];
                    if (oldLayer && newLayer) {
                        const checkLayers = this.framework.checkIsExtraGroup(oldLayer)
                            ? [...oldLayer.children]
                            : [oldLayer];
                        traverseTrees(checkLayers, (item) => {
                            const latestLayer = this.framework.getLayer(item.id, true);
                            if (latestLayer) {
                                if (latestLayer.parentId) {
                                    const parent = this.framework.getLayer(latestLayer.parentId);
                                    if (parent) {
                                        oldFlatLayers.push(item);
                                        newFlatLayers.push(latestLayer);
                                    } else {
                                        historyItem.isDirty = true;
                                    }
                                } else {
                                    oldFlatLayers.push(item);
                                    newFlatLayers.push(latestLayer);
                                }
                            } else {
                                console.log('ot history dirty 1', item.id);
                                historyItem.isDirty = true;
                            }
                        });
                        activeId = newLayer.id;
                    } else if (oldLayers && newLayers) {
                        traverseTrees(oldLayers, (item) => {
                            const latestLayer = this.framework.getLayer(item.id, true);
                            if (latestLayer) {
                                if (latestLayer.parentId) {
                                    const parent = this.framework.getLayer(latestLayer.parentId);
                                    if (parent) {
                                        oldFlatLayers.push(item);
                                        newFlatLayers.push(latestLayer);
                                    } else {
                                        historyItem.isDirty = true;
                                    }
                                } else {
                                    oldFlatLayers.push(item);
                                    newFlatLayers.push(latestLayer);
                                }
                            } else {
                                historyItem.isDirty = true;
                            }
                        });
                    }
                    if (!historyItem.isDirty) {
                        const activeLayer = this.framework.getLayer(activeId);
                        if (!activeLayer) {
                            this.framework.operate.delActiveLayer();
                        }
                    }
                    this.redoUndoStore.set(historyCursor, {
                        isDirty: historyItem.isDirty,
                        oldFlatLayers,
                        newFlatLayers,
                        activeId,
                    });
                    // console.log('ot redoUndoStore', historyCursor, historyItem.isDirty, {
                    //     oldFlatLayers,
                    //     newFlatLayers,
                    //     activeId,
                    // });
                }
                if (isRedo) {
                    const beforeData = this.redoUndoStore.get(historyCursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty && beforeData.oldFlatLayers && beforeData.newFlatLayers) {
                        const data = historyItem.data as IHistoryDefaultData;
                        const { oldFlatLayers, newFlatLayers } = beforeData;
                        data.oldLayer = undefined;
                        data.oldLayers = oldFlatLayers;
                        data.newLayer = undefined;
                        data.newLayers = newFlatLayers;
                    }
                }
            }
        });
    }

    /** 绑定撤销、重做后的钩子，发送给其他用户 */
    private bindOnAfterRedoUndo() {
        // 撤销、重做后，发送给其他用户
        this.framework.history.onChange(({ isMain, data, isCustom, isRedo, isUndo }, historyItem) => {
            if (this.room.isRealEdit() && isMain && (isRedo || isUndo) && historyItem) {
                const args: any = { isCustom, isDirty: historyItem.isDirty };
                if ((historyItem?.data as IHistoryCustomData)?.type) {
                    args.type = (historyItem?.data as IHistoryCustomData)?.type;
                }
                if (isRedo) args.isRedo = true;
                if (isUndo) args.isUndo = true;
                console.log('ot history onChange', args);
                if (historyItem.isDirty) {
                    return;
                }
                // 自定义历史记录
                if (isCustom) {
                    const { type } = data as IHistoryCustomData;
                    const res = this.syncOptions[type]?.onAfterRedoUndo?.(
                        historyItem,
                        isRedo,
                        isUndo,
                        this.redoUndoStore,
                    );
                    if (isNull(res)) return;
                    this.syncOptions[type]?.onMerge?.(historyItem, true);
                    if (this.syncOptions[type]?.isLikeDefault && res?.message[0]) {
                        // console.log('ot 自定义历史记录 1', data, res, historyItem.serverTime);
                        this.processDiffInfoVersion(res.message[0], historyItem.serverTime);
                    }
                    const operateData = [
                        0, // isSend
                        0, // isWrite
                        isRedo,
                        isUndo,
                    ];
                    if (historyItem.needSyncRenderIds) {
                        this.room.syncRenderIdsAdapter.set(this.room.collaborators.getWsKey(), historyItem.originIndex);
                        operateData.push(historyItem.originIndex);
                    }
                    if (res) {
                        const { message, needBeforeData } = res;
                        const beforeData = this.redoUndoStore.get(historyItem.cursor);
                        this.room.sendToAll(OperateType.HistoryCustom, [
                            type,
                            operateData,
                            message,
                            needBeforeData === false ? null : beforeData,
                            historyItem.serverTime,
                            historyItem.id,
                        ]);
                    } else {
                        this.room.sendToAll(OperateType.HistoryCustom, [
                            type,
                            operateData,
                            historyItem,
                            this.redoUndoStore.get(historyItem.cursor),
                        ]);
                    }
                    return;
                }
                // console.log('ot history onChange 1', cloneDeep(data));
                // 默认历史记录
                const diffInfo = this.getDifferentInfo(data, isRedo, isUndo, new Set(['parentId']));
                if (!Object.keys(diffInfo).length) return;
                // console.log('ot history onChange 2', cloneDeep(diffInfo));
                this.processDiffInfoVersion(diffInfo, historyItem.serverTime);
                this.room.sendToAll(OperateType.HistoryDefault, [
                    [
                        0, // isWrite
                        isRedo,
                        isUndo,
                    ],
                    diffInfo,
                    historyItem.serverTime,
                    historyItem.id,
                ]);
            }
        }, -1);
    }

    /** 绑定接收消息 */
    private bindOnMessage() {
        this.room.onMessage(
            OperateType.HistoryDefault,
            ({ fromWsMemberKey, data }: IEditMessage<[number[], Record<string, any>, number, string]>) => {
                const [[isWrite, isRedo, isUndo], diffInfo, serverTime, id] = data;
                this.processMessage({
                    id,
                    serverTime,
                    data: diffInfo,
                    isWrite: Boolean(isWrite),
                    isRedo: Boolean(isRedo),
                    isUndo: Boolean(isUndo),
                    isSend: false,
                    type: this.defaultHistoryHooksKey,
                    fromWsMemberKey,
                });
            },
        );
        this.room.onMessage(
            OperateType.HistoryCustom,
            ({ fromWsMemberKey, data }: IEditMessage<[string, number[], any, any, number, string]>) => {
                const [type, [isSend, isWrite, isRedo, isUndo, originIndex], res, beforeData, serverTime, id] = data;
                this.processMessage({
                    id: id || res?.id,
                    serverTime: serverTime || res?.serverTime,
                    data: res,
                    isWrite: Boolean(isWrite),
                    isRedo: Boolean(isRedo),
                    isUndo: Boolean(isUndo),
                    isSend: Boolean(isSend),
                    type,
                    beforeData,
                    originIndex,
                    fromWsMemberKey,
                });
            },
        );
    }

    /** 处理接收到的历史记录 */
    private processMessage(item: IDocumentHistoryItem, isReceive = true) {
        if (isReceive) {
            if (item.isSend) {
                if (!this.syncOptions[item.type]?.onReceive) {
                    console.error(`未找到类型为${item.type}的同步选项`);
                    return;
                }
                this.syncOptions[item.type].onReceive?.(item.data);
                return;
            }
            if (!this.isReady) {
                this.historyBuffer.push(item);
                return;
            }
        }
        console.log('ot 收到', item.type, item.id, item.serverTime, isReceive, item.data);

        // 相同操作，只保留最新的
        if (item.type === this.defaultHistoryHooksKey) {
            // 处理默认历史记录
            item.data = this.processDiffInfoVersion(item.data, item.serverTime);
            this.applyMessage(item);
        } else {
            // 收到的脏数据，不需要处理直接丢弃
            const isDirtyMessage = this.syncOptions[item.type]?.onMerge?.(item, false);
            if (isDirtyMessage) {
                console.log('ot isDirtyMessage');
                if (isNumber(item.originIndex) && item.fromWsMemberKey) {
                    this.room.syncRenderIdsAdapter.set(item.fromWsMemberKey, item.originIndex);
                }
                return;
            }
            if (this.syncOptions[item.type]?.isLikeDefault) {
                item.data[0] = this.processDiffInfoVersion(item.data[0], item.serverTime);
            }
            this.applyMessage(item);
            if (isNumber(item.originIndex) && item.fromWsMemberKey) {
                this.room.syncRenderIdsAdapter.set(item.fromWsMemberKey, item.originIndex);
            }
        }
        console.log('ot 应用完成');
    }

    /** 应用历史记录 */
    private applyMessage(item: IDocumentHistoryItem) {
        if (!this.syncOptions[item.type]) {
            console.error(`未找到类型为${item.type}的同步选项`);
            return;
        }
        // console.log('ot 应用', item.data, item.isRedo, item.isUndo, item.isWrite, item.beforeData);
        this.syncOptions[item.type].onMessage(item.data, item.isRedo, item.isUndo, item.isWrite, item.beforeData);
    }

    /** 处理图层属性差异信息版本 */
    private processDiffInfoVersion(diffInfo: Record<string, any>, serverTime: number) {
        const newData: Record<string, any> = {};
        Object.keys(diffInfo).forEach((layerId) => {
            const oldAttrs = diffInfo[layerId];
            const latestLayerVersion = this.framework.getLayer(layerId)?.__renderLayerVersion || 0;
            if (latestLayerVersion > serverTime) {
                console.log('ot 丢弃应用');
                return;
            }
            const newAttrs = {};
            const keys = this.getObjectKeys(oldAttrs);
            // console.log('ot keys', keys);
            for (const key of keys) {
                const res = this.framework.setRealEditTypeVersion(layerId, key, serverTime);
                if (res) {
                    // set(newAttrs, key, get(oldAttrs, key));
                    const attrs = key.split('.');
                    let current = newAttrs;
                    // 除了最后一个属性，其他属性需要创建对象
                    for (let i = 0; i < attrs.length - 1; i++) {
                        const attr = attrs[i];
                        current[attr] = current[attr] || {};
                        current = current[attr];
                    }
                    // 最后一个属性直接赋值
                    const lastAttr = attrs[attrs.length - 1];
                    current[lastAttr] = attrs.reduce((acc, cur) => acc[cur], oldAttrs);
                }
            }
            if (Object.keys(newAttrs).length) {
                newData[layerId] = newAttrs;
            }
        });

        return newData;
    }

    /** 获取对象的属性键 */
    private getObjectKeys(obj: Record<string, any>, prefix = '') {
        const keys: string[] = [];
        Object.keys(obj).forEach((key) => {
            const currentKey = prefix ? `${prefix}.${key}` : key;
            const value = obj[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                keys.push(...this.getObjectKeys(value, currentKey));
            } else {
                keys.push(currentKey);
            }
        });
        return keys;
    }

    /** 处理差异信息 */
    private processDifferentInfo(
        diffInfo: Record<string, any>,
        syncMessage = false,
        originLayerRecord?: Record<string, ILayerProps>,
    ) {
        const keys: string[] = [];
        // console.log('ot default history receive');
        Object.keys(diffInfo).forEach((layerId) => {
            keys.push(layerId);
            const differentProps = diffInfo[layerId];
            let layer = originLayerRecord?.[layerId];
            if (!layer) {
                layer = this.framework.getLayer(layerId, true);
            }
            if (!layer) return;
            // 应用差异属性到图层
            applyDifferentProps(layer, differentProps, this.framework.getLayer(layerId));
            if (syncMessage) {
                const { originIndex } = parseLayerId(layerId);
                if (isNumber(originIndex)) {
                    // console.log('ot processDifferentInfo 1', cloneDeep(layer));
                    this.framework.stage.addOrUpdate(layer, false);
                    this.framework.setRerender(originIndex);
                }
            } else {
                // console.log('ot processDifferentInfo 2', cloneDeep(layer));
                this.framework.stage.addOrUpdate(layer, false);
            }
        });
        this.framework.rerender();
        return keys;
    }

    /** 获取默认历史记录数据里的差异信息 */
    private getDifferentInfo(
        data: IHistoryDefaultData,
        isRedo: boolean,
        isUndo: boolean,
        skipAttrs: Set<string> = new Set(),
    ) {
        const { newLayer, newLayers, oldLayer, oldLayers } = data;
        // 记录每个图层的差异信息
        let differentLayerProps: Record<string, any> = {};
        if (newLayer && oldLayer) {
            if (isRedo) {
                differentLayerProps = getDifferentProps(newLayer, oldLayer, undefined, skipAttrs);
            }
            if (isUndo) {
                differentLayerProps = getDifferentProps(oldLayer, newLayer, undefined, skipAttrs);
            }
        } else if (newLayers && oldLayers) {
            // 多个图层
            for (let i = 0; i < newLayers.length; i++) {
                const newLayer = newLayers[i];
                const oldLayer = oldLayers[i];
                let differentProps = {};
                if (isRedo) {
                    differentProps = getDifferentProps(newLayer, oldLayer, differentLayerProps, skipAttrs);
                }
                if (isUndo) {
                    differentProps = getDifferentProps(oldLayer, newLayer, differentLayerProps, skipAttrs);
                }
                differentLayerProps = { ...differentLayerProps, ...differentProps };
            }
        }

        return differentLayerProps;
    }
}
